// @flow
import * as React from 'react'
import SearchableSelectField from '../SearchableSelectField'
import { debounce, isNil } from '../../../utils'

// Types
import type { Props as SearchableSelectFieldProps } from '../SearchableSelectField'

export type Props = SearchableSelectFieldProps
type State = {
  error: ?string,
  loading: boolean,
  options: Array<any>,
  query: ?string,
}

export type QueryChangeOptions = {
  loading?: boolean,
  keepOptions?: boolean,
}

/**
 * A wrapper around SearchableSelectField which adds all the logic
 * around actual getting the select options, handling search query change,
 * showing loading indicator when necessary, etc.
 */
class ControlledSearchableSelectField extends React.Component<Props, State> {
  iteration = 0

  state = {
    error: null,
    loading: false,
    options: [],
    query: null,
  }

  static defaultProps = SearchableSelectField.defaultProps

  UNSAFE_componentWillMount() {
    const { emptyValue } = this.props
    const { query, requestOptionsOnMount } = this.props.searchableOptions
    const { value } = this.props.input
    const initQuery = query || null

    this.setState(
      {
        query: initQuery,
        options: this.props.options,
      },
      () => {
        if (isNil(value, emptyValue) || requestOptionsOnMount) {
          this.getNextOptions()
        }
      }
    )
  }

  componentWillUnmount() {
    this.iteration = -1
    this.getNextOptions.cancel()
  }

  handleQueryChange = (
    query: ?string,
    queryChangeOptions: ?QueryChangeOptions
  ) => {
    const { loading = true, keepOptions = false } = queryChangeOptions || {}
    const { onQueryChange } = this.props.searchableOptions
    const nextState = {
      query,
      loading,
      error: null,
      options: !keepOptions && isNil(query) ? [] : this.state.options,
    }

    this.setState(nextState, () => !keepOptions && this.getNextOptions())
    onQueryChange && onQueryChange(query)
  }

  handleRequestError(error: ?Error) {
    const errorMessage =
      error && error.message && typeof error.message === 'string'
        ? error.message
        : 'Unkown error occured while getting suggestions'

    this.setState({
      loading: false,
      error: errorMessage,
    })
  }

  handleRequestSuccess(options: ?Array<any>) {
    const normQuery = this.getNormalizedQuery()
    const {
      maxOptionsLength,
      transformOptions,
      renderEmpty,
    } = this.props.searchableOptions
    let finalOptions = options || []

    if (maxOptionsLength && finalOptions.length > maxOptionsLength) {
      finalOptions = finalOptions.slice(0, maxOptionsLength)
    }
    if (transformOptions) {
      finalOptions = transformOptions(finalOptions, normQuery)
    }
    if (renderEmpty && !finalOptions.length) {
      const emptyContent = renderEmpty(normQuery)
      finalOptions = emptyContent ? [emptyContent] : []
    }

    this.setState({
      loading: false,
      options: finalOptions,
    })
  }

  getNextOptions = debounce(330, () => {
    const { requestOptions } = this.props.searchableOptions
    if (!requestOptions) return

    const requestIteration = ++this.iteration
    const isSameIteration = () => requestIteration === this.iteration
    const normQuery = this.getNormalizedQuery()

    this.setState({ loading: true })
    const request = requestOptions(normQuery)
    Promise.resolve(request).then(
      (options) => isSameIteration() && this.handleRequestSuccess(options),
      (error) => isSameIteration() && this.handleRequestError(error)
    )
  })

  getNormalizedQuery() {
    const query = this.state.query || ''
    return query.trim()
  }

  getMeta() {
    const { meta } = this.props
    return {
      ...meta,
      loading: meta.loading || this.state.loading,
      error: meta.error || this.state.error,
    }
  }

  getSearchableOptions() {
    return {
      ...this.props.searchableOptions,
      query: this.state.query,
      onQueryChange: this.handleQueryChange,
    }
  }

  render() {
    return (
      <SearchableSelectField
        {...this.props}
        options={this.state.options}
        searchableOptions={this.getSearchableOptions()}
        meta={this.getMeta()}
      />
    )
  }
}

export default ControlledSearchableSelectField
